import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, MessageSquare, LogOut, FileText, User, Calendar, Briefcase, Users } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useIsArtisan } from "@/hooks/useArtisanProfile";
import { useUnreadCount } from "@/hooks/useUnreadCount";
import { usePendingAppointmentsCount } from "@/hooks/usePendingAppointmentsCount";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useLanguageContext } from "@/contexts/LanguageContext";
import { useLocalizedNavigate, useLocalizedPath } from "@/hooks/useLocalizedNavigate";

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { locale, isRTL } = useLanguageContext();
  const navigate = useLocalizedNavigate();
  const lp = useLocalizedPath();
  const { user, signOut } = useAuth();

  // Naviguer vers la racine de la langue courante d'abord pour éviter le flash
  const handleSignOut = async () => {
    navigate("/");
    await signOut();
  };

  const { data: isArtisan, isLoading: isArtisanLoading } = useIsArtisan();
  const unreadCount = useUnreadCount();
  const pendingRdv = usePendingAppointmentsCount();

  // Vérifie si le pathname est actif (avec ou sans préfixe de langue)
  const isActive = (path: string) => {
    const localizedPath = lp(path);
    return location.pathname === localizedPath || location.pathname === localizedPath + "/";
  };

  const clientPublicLinks = [
    { to: "/", label: locale === "ar" ? "الرئيسية" : "Accueil" },
    { to: "/annonces", label: locale === "ar" ? "الإعلانات" : "Annonces" },
    { to: "/recherche-artisans", label: locale === "ar" ? "الحرفيون" : "Artisans" },
    { to: "/poster-annonce", label: locale === "ar" ? "نشر إعلان" : "Poster une annonce" },
    { to: "/devis", label: locale === "ar" ? "عرض سعر" : "Devis" },
  ];

  return (
    <nav className="sticky top-0 z-50 border-b border-border/60 bg-card/90 backdrop-blur-md" dir={isRTL ? "rtl" : "ltr"}>
      <div className="container flex h-16 items-center justify-between">

        {/* Logo */}
        <Link
          to={isArtisan ? lp("/dashboard") : lp("/")}
          className="flex items-center shrink-0"
          aria-label="Hirafi — Accueil"
        >
          <img
            src={locale === "ar" ? "/logo-ar.png" : "/logo-fr.png"}
            alt="Hirafi"
            height={22}
            draggable={false}
            className="h-[22px] w-auto"
          />
        </Link>

        {/* Desktop — liens publics client uniquement */}
        {!user && (
          <div className="hidden items-center gap-0.5 md:flex">
            {clientPublicLinks.map((link) => (
              <Link key={link.to} to={lp(link.to)}>
                <Button
                  variant={isActive(link.to) ? "secondary" : "ghost"}
                  size="sm"
                  className="font-medium"
                  aria-current={isActive(link.to) ? "page" : undefined}
                >
                  {link.label}
                </Button>
              </Link>
            ))}
          </div>
        )}

        {/* Desktop — actions auth + switcher de langue */}
        <div className="hidden items-center gap-1.5 md:flex">
          <LanguageSwitcher />

          {!user ? (
            <>
              <Link to={lp("/connexion")}>
                <Button variant="outline" size="sm">
                  {locale === "ar" ? "تسجيل الدخول" : "Connexion"}
                </Button>
              </Link>
              <Link to={lp("/inscription-client")}>
                <Button variant="hero" size="sm">
                  {locale === "ar" ? "إنشاء حساب" : "S'inscrire"}
                </Button>
              </Link>
            </>
          ) : isArtisanLoading ? (
            <div className="h-8 w-48 rounded-lg bg-muted animate-pulse" aria-hidden="true" />
          ) : isArtisan ? (
            /* ── Nav Artisan ── */
            <>
              <Link to={lp("/annonces")}>
                <Button
                  variant={isActive("/annonces") ? "secondary" : "ghost"}
                  size="sm"
                  className={cn("gap-2 font-medium", isActive("/annonces") && "text-primary")}
                  aria-current={isActive("/annonces") ? "page" : undefined}
                >
                  <Briefcase className="h-4 w-4" />
                  {locale === "ar" ? "المهمات" : "Missions"}
                </Button>
              </Link>

              <Link to={lp("/messagerie")}>
                <Button
                  variant={isActive("/messagerie") ? "secondary" : "ghost"}
                  size="sm"
                  className="gap-2 relative"
                  aria-label={locale === "ar"
                    ? `الرسائل${unreadCount > 0 ? `، ${unreadCount} غير مقروءة` : ""}`
                    : `Messages${unreadCount > 0 ? `, ${unreadCount} non lu${unreadCount > 1 ? "s" : ""}` : ""}`}
                  aria-current={isActive("/messagerie") ? "page" : undefined}
                >
                  <MessageSquare className="h-4 w-4" />
                  {locale === "ar" ? "الرسائل" : "Messages"}
                  {unreadCount > 0 && (
                    <Badge className="h-5 min-w-5 rounded-full bg-destructive px-1.5 text-[10px] text-destructive-foreground leading-none">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </Badge>
                  )}
                </Button>
              </Link>

              <Link to={lp("/mes-rendez-vous")}>
                <Button
                  variant={isActive("/mes-rendez-vous") ? "secondary" : "ghost"}
                  size="sm"
                  className={cn("gap-2 relative", isActive("/mes-rendez-vous") && "text-primary")}
                  aria-current={isActive("/mes-rendez-vous") ? "page" : undefined}
                >
                  <Calendar className="h-4 w-4" />
                  {locale === "ar" ? "المواعيد" : "RDV"}
                  {pendingRdv > 0 && (
                    <Badge className="h-5 min-w-5 rounded-full bg-warning px-1.5 text-[10px] text-warning-foreground leading-none">
                      {pendingRdv}
                    </Badge>
                  )}
                </Button>
              </Link>

              <Link to={lp("/mes-devis")}>
                <Button
                  variant={isActive("/mes-devis") ? "secondary" : "ghost"}
                  size="sm"
                  className={cn("gap-2", isActive("/mes-devis") && "text-primary")}
                  aria-current={isActive("/mes-devis") ? "page" : undefined}
                >
                  <FileText className="h-4 w-4" />
                  {locale === "ar" ? "عروض الأسعار" : "Devis"}
                </Button>
              </Link>

              <Link to={lp("/mon-profil-artisan")}>
                <Button
                  variant={isActive("/mon-profil-artisan") ? "secondary" : "ghost"}
                  size="sm"
                  className="gap-2"
                  aria-current={isActive("/mon-profil-artisan") ? "page" : undefined}
                >
                  <User className="h-4 w-4" />
                  {locale === "ar" ? "الملف الشخصي" : "Profil"}
                </Button>
              </Link>

              <Button variant="outline" size="sm" onClick={handleSignOut} className="gap-2 ms-1">
                <LogOut className="h-4 w-4" />
                {locale === "ar" ? "تسجيل الخروج" : "Déconnexion"}
              </Button>
            </>
          ) : (
            /* ── Nav Client connecté ── */
            <>
              <Link to={lp("/mes-annonces")}>
                <Button
                  variant={isActive("/mes-annonces") ? "secondary" : "ghost"}
                  size="sm"
                  className="gap-2"
                  aria-current={isActive("/mes-annonces") ? "page" : undefined}
                >
                  <FileText className="h-4 w-4" />
                  {locale === "ar" ? "إعلاناتي" : "Mes annonces"}
                </Button>
              </Link>

              <Link to={lp("/recherche-artisans")}>
                <Button
                  variant={isActive("/recherche-artisans") ? "secondary" : "ghost"}
                  size="sm"
                  className="gap-2"
                  aria-current={isActive("/recherche-artisans") ? "page" : undefined}
                >
                  <Users className="h-4 w-4" />
                  {locale === "ar" ? "الحرفيون" : "Artisans"}
                </Button>
              </Link>

              <Link to={lp("/messagerie")}>
                <Button
                  variant={isActive("/messagerie") ? "secondary" : "ghost"}
                  size="sm"
                  className="gap-2 relative"
                  aria-current={isActive("/messagerie") ? "page" : undefined}
                >
                  <MessageSquare className="h-4 w-4" />
                  {locale === "ar" ? "الرسائل" : "Messages"}
                  {unreadCount > 0 && (
                    <Badge className="h-5 min-w-5 rounded-full bg-destructive px-1.5 text-[10px] text-destructive-foreground leading-none">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </Badge>
                  )}
                </Button>
              </Link>

              <Link to={lp("/mon-profil")}>
                <Button
                  variant={isActive("/mon-profil") ? "secondary" : "ghost"}
                  size="sm"
                  className="gap-2"
                  aria-current={isActive("/mon-profil") ? "page" : undefined}
                >
                  <User className="h-4 w-4" />
                  {locale === "ar" ? "الملف الشخصي" : "Profil"}
                </Button>
              </Link>

              <Button variant="outline" size="sm" onClick={handleSignOut} className="gap-2 ms-1">
                <LogOut className="h-4 w-4" />
                {locale === "ar" ? "تسجيل الخروج" : "Déconnexion"}
              </Button>
            </>
          )}
        </div>

        {/* Mobile — switcher langue + hamburger (toujours visibles) */}
        <div className="flex items-center gap-2 md:hidden">
          <LanguageSwitcher />
          {!user && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen
                ? (locale === "ar" ? "إغلاق القائمة" : "Fermer le menu")
                : (locale === "ar" ? "فتح القائمة" : "Ouvrir le menu")}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          )}
        </div>
      </div>

      {/* Mobile menu — visiteurs uniquement */}
      {mobileOpen && !user && (
        <div className="border-t bg-card px-4 pb-4 pt-3 md:hidden animate-fade-in">
          <div className="flex flex-col gap-1">
            {clientPublicLinks.map((link) => (
              <Link key={link.to} to={lp(link.to)} onClick={() => setMobileOpen(false)}>
                <Button
                  variant={isActive(link.to) ? "secondary" : "ghost"}
                  className="w-full justify-start font-medium"
                  aria-current={isActive(link.to) ? "page" : undefined}
                >
                  {link.label}
                </Button>
              </Link>
            ))}
            <div className="mt-3 flex flex-col gap-2 border-t pt-3">
              <Link to={lp("/connexion")} onClick={() => setMobileOpen(false)}>
                <Button variant="outline" className="w-full">
                  {locale === "ar" ? "تسجيل الدخول" : "Connexion"}
                </Button>
              </Link>
              <Link to={lp("/inscription-client")} onClick={() => setMobileOpen(false)}>
                <Button variant="hero" className="w-full">
                  {locale === "ar" ? "إنشاء حساب مجاني" : "S'inscrire gratuitement"}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
